<template>
  <div class="sys-dict-data-config">
    <div class="rj-page-card">
      <div class="rj-page-card__title">
        字典字段管理
      </div>
      <div class="rj-page-card__content">
        <p class="page-tip">
          维护 sys_dict_data 字典数据。左侧选类型，右侧增删改查具体字段项。业务读取一般只取状态为「正常」的项。
        </p>

        <div class="panel-layout">
          <div class="type-panel">
            <div class="panel-head">
              <span class="panel-title">字典类型</span>
              <el-button size="mini" :loading="typeLoading" @click="loadTypes">
                刷新
              </el-button>
            </div>
            <el-input
              v-model.trim="typeKeyword"
              clearable
              size="small"
              class="filter-input"
              placeholder="筛选字典类型"
              @keyup.enter.native="loadTypes"
              @clear="loadTypes"
            />
            <el-table
              v-loading="typeLoading"
              :data="dictTypes"
              border
              highlight-current-row
              height="500"
              class="type-table"
              @current-change="onTypeChange"
            >
              <el-table-column prop="dictType" label="类型编码" min-width="160" show-overflow-tooltip />
            </el-table>
          </div>

          <div class="item-panel">
            <div class="panel-head">
              <span class="panel-title">
                字典项
                <span v-if="currentType" class="current-type">· {{ currentType }}</span>
                <span v-else class="current-type muted">· 请先选择左侧类型</span>
              </span>
              <el-button
                type="primary"
                size="mini"
                :disabled="!currentType"
                @click="openDialog()"
              >
                新增
              </el-button>
            </div>

            <div class="filter-bar">
              <el-input
                v-model.trim="itemKeyword"
                clearable
                size="small"
                class="filter-input"
                placeholder="标签/键值/备注"
                :disabled="!currentType"
                @keyup.enter.native="loadItems"
              />
              <el-select
                v-model="itemStatus"
                clearable
                size="small"
                class="status-select"
                placeholder="状态"
                :disabled="!currentType"
              >
                <el-option label="正常" value="0" />
                <el-option label="停用" value="1" />
              </el-select>
              <el-button type="primary" size="mini" :disabled="!currentType" :loading="itemLoading" @click="loadItems">
                查询
              </el-button>
              <el-button size="mini" :disabled="!currentType" @click="resetItemFilter">
                重置
              </el-button>
            </div>

            <el-table v-loading="itemLoading" :data="pagedItems" border>
              <el-table-column type="index" label="序号" width="55" :index="indexOffset" />
              <el-table-column prop="dictSort" label="排序" width="70" />
              <el-table-column prop="dictLabel" label="标签" min-width="140" show-overflow-tooltip />
              <el-table-column prop="dictValue" label="键值" min-width="180" show-overflow-tooltip />
              <el-table-column prop="status" label="状态" width="80">
                <template v-slot="{ row }">
                  {{ row.status === '1' ? '停用' : '正常' }}
                </template>
              </el-table-column>
              <el-table-column prop="isDefault" label="默认" width="70">
                <template v-slot="{ row }">
                  {{ row.isDefault === 'Y' ? '是' : '否' }}
                </template>
              </el-table-column>
              <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
              <el-table-column label="操作" width="120" fixed="right">
                <template v-slot="{ row }">
                  <el-button type="text" @click="openDialog(row)">
                    编辑
                  </el-button>
                  <el-button type="text" class="danger-text" @click="confirmDelete(row)">
                    删除
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              v-if="items.length > 0"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="items.length"
              :page-size="pageSize"
              :page-sizes="[10, 20, 50, 100]"
              :current-page="pageNum"
              class="pagination"
              @size-change="onSizeChange"
              @current-change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="560px"
      :close-on-click-modal="false"
      @closed="onDialogClosed"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="字典类型" prop="dictType">
          <el-input v-model.trim="form.dictType" :disabled="!!form.id" clearable placeholder="如 overseas_refund_intn_mail" />
        </el-form-item>
        <el-form-item label="标签" prop="dictLabel">
          <el-input v-model.trim="form.dictLabel" clearable maxlength="100" show-word-limit placeholder="dict_label" />
        </el-form-item>
        <el-form-item label="键值" prop="dictValue">
          <el-input
            v-model.trim="form.dictValue"
            type="textarea"
            :rows="3"
            maxlength="500"
            show-word-limit
            placeholder="dict_value"
          />
        </el-form-item>
        <el-form-item label="排序" prop="dictSort">
          <el-input-number v-model="form.dictSort" :min="0" :max="9999" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio label="0">
              正常
            </el-radio>
            <el-radio label="1">
              停用
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="是否默认" prop="isDefault">
          <el-radio-group v-model="form.isDefault">
            <el-radio label="N">
              否
            </el-radio>
            <el-radio label="Y">
              是
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model.trim="form.remark" type="textarea" :rows="2" maxlength="500" show-word-limit />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">
          取 消
        </el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">
          确 定
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  listSysDictTypes,
  listSysDictByType,
  createSysDictData,
  updateSysDictData,
  deleteSysDictData
} from '@/API';

export default {
  name: 'SysDictDataConfig',
  data () {
    return {
      typeLoading: false,
      itemLoading: false,
      saving: false,
      typeKeyword: '',
      dictTypes: [],
      currentType: '',
      itemKeyword: '',
      itemStatus: '',
      items: [],
      pageNum: 1,
      pageSize: 10,
      dialogVisible: false,
      form: this.emptyForm(),
      rules: {
        dictType: [{ required: true, message: '请输入字典类型', trigger: 'blur' }],
        dictLabel: [{ required: true, message: '请输入标签', trigger: 'blur' }],
        dictValue: [{ required: true, message: '请输入键值', trigger: 'blur' }]
      }
    };
  },
  computed: {
    dialogTitle () {
      return this.form.id ? '编辑字典项' : '新增字典项';
    },
    pagedItems () {
      const start = (this.pageNum - 1) * this.pageSize;
      return this.items.slice(start, start + this.pageSize);
    },
    indexOffset () {
      return (this.pageNum - 1) * this.pageSize + 1;
    }
  },
  mounted () {
    this.loadTypes();
  },
  methods: {
    emptyForm () {
      return {
        id: null,
        dictType: '',
        dictLabel: '',
        dictValue: '',
        dictSort: 0,
        status: '0',
        isDefault: 'N',
        remark: ''
      };
    },
    async loadTypes () {
      this.typeLoading = true;
      try {
        const { data } = await listSysDictTypes(this.typeKeyword);
        if (data && data.status === '200') {
          const list = data.data || [];
          this.dictTypes = list.map(t => ({ dictType: t }));
          if (this.currentType && !list.includes(this.currentType)) {
            this.currentType = '';
            this.items = [];
          }
        }
      } finally {
        this.typeLoading = false;
      }
    },
    onTypeChange (row) {
      this.currentType = row ? row.dictType : '';
      this.itemKeyword = '';
      this.itemStatus = '';
      this.pageNum = 1;
      if (this.currentType) {
        this.loadItems();
      } else {
        this.items = [];
      }
    },
    resetItemFilter () {
      this.itemKeyword = '';
      this.itemStatus = '';
      this.pageNum = 1;
      this.loadItems();
    },
    async loadItems () {
      if (!this.currentType) {
        return;
      }
      this.itemLoading = true;
      try {
        const params = { dictType: this.currentType };
        if (this.itemKeyword) {
          params.keyword = this.itemKeyword;
        }
        if (this.itemStatus !== '' && this.itemStatus != null) {
          params.status = this.itemStatus;
        }
        const { data } = await listSysDictByType(params);
        if (data && data.status === '200') {
          this.items = data.data || [];
          this.pageNum = 1;
        }
      } finally {
        this.itemLoading = false;
      }
    },
    onPageChange (page) {
      this.pageNum = page;
    },
    onSizeChange (size) {
      this.pageSize = size;
      this.pageNum = 1;
    },
    async openDialog (row) {
      if (row) {
        this.form = {
          id: row.id,
          dictType: row.dictType,
          dictLabel: row.dictLabel,
          dictValue: row.dictValue,
          dictSort: row.dictSort == null ? 0 : Number(row.dictSort),
          status: row.status || '0',
          isDefault: row.isDefault || 'N',
          remark: row.remark || ''
        };
      } else {
        const nextSort = await this.calcNextSort();
        this.form = {
          id: null,
          dictType: this.currentType || '',
          dictLabel: '',
          dictValue: '',
          dictSort: nextSort,
          status: '0',
          isDefault: 'N',
          remark: ''
        };
      }
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      });
    },
    /** 拉服务端全量列表算最大排序 + 1，失败时用当前已加载列表兜底 */
    async calcNextSort () {
      try {
        const { data } = await listSysDictByType({ dictType: this.currentType });
        const list = (data && data.status === '200' && data.data) ? data.data : this.items;
        return this.maxSortOf(list) + 1;
      } catch (e) {
        return this.maxSortOf(this.items) + 1;
      }
    },
    maxSortOf (list) {
      return (list || []).reduce((max, item) => {
        const sort = item && item.dictSort != null ? Number(item.dictSort) : 0;
        return Number.isFinite(sort) && sort > max ? sort : max;
      }, 0);
    },
    onDialogClosed () {
      this.form = this.emptyForm();
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      });
    },
    submitForm () {
      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          return;
        }
        this.saving = true;
        try {
          const savedType = this.form.dictType;
          const isEdit = !!this.form.id;
          const api = isEdit ? updateSysDictData : createSysDictData;
          const { data } = await api({ ...this.form });
          if (data && data.status === '200') {
            this.$message.success(isEdit ? '更新成功' : '新增成功');
            this.dialogVisible = false;
            await this.loadTypes();
            if (!this.currentType && savedType) {
              this.currentType = savedType;
            }
            if (this.currentType) {
              await this.loadItems();
            }
          }
        } finally {
          this.saving = false;
        }
      });
    },
    confirmDelete (row) {
      this.$confirm(`确认删除字典项「${row.dictLabel}」？删除后不可恢复。`, '提示', { type: 'warning' })
        .then(async () => {
          const { data } = await deleteSysDictData({ id: row.id });
          if (data && data.status === '200') {
            this.$message.success('删除成功');
            await this.loadItems();
            await this.loadTypes();
          }
        })
        .catch(() => {});
    }
  }
};
</script>

<style lang="scss" scoped>
.page-tip {
  margin: 0 0 12px;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.panel-layout {
  display: flex;
  gap: 16px;
}

.type-panel {
  width: 280px;
  flex-shrink: 0;
}

.item-panel {
  flex: 1;
  min-width: 0;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.panel-title {
  font-weight: 600;
}

.current-type {
  font-weight: 400;
  color: #409eff;
  margin-left: 4px;

  &.muted {
    color: #999;
  }
}

.filter-input {
  margin-bottom: 10px;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  .filter-input {
    width: 220px;
    margin-bottom: 0;
  }

  .status-select {
    width: 110px;
  }
}

.danger-text {
  color: #f56c6c;
}

.pagination {
  margin-top: 12px;
  text-align: right;
}
</style>
